import React, { useState, useEffect } from 'react'
import { useTranslate, useNotify, useRefresh, FormDataConsumer } from 'react-admin'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  FormControl,
  MenuItem,
  Select,
  Divider,
  Paper,
  TextField,
} from '@mui/material'

import EditIcon from '@mui/icons-material/Edit'
import { useForm, FormProvider, useWatch, useFormContext } from 'react-hook-form'
import { HasPermission, hasPermissionFunction } from 'src/helper/HasPermission'
import { PERMISSION_EDIT, PERMISSION_RECEIPT } from 'src/helper/Permission'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { LoadingButton } from '@mui/lab'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { paymentServices } from 'src/services/payment/Payment.service'
import { customAwait } from 'src/components/pages/sales/reservation/helper/customAwait'
import { userServices } from 'src/services/reservation/user/user.service'
import { reverseExchangeRate } from 'src/components/pages/payment/payment/logic/common/calculateReverseExchangeRate'
import { itemServices } from 'src/services/reservation/item/item.service'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { usePaymentContext } from 'src/components/pages/payment/payment/context/paymentContext'
import { PaymentProviderContext } from 'src/components/pages/payment/payment/context/PaymentProvider'
import { extractDetails } from 'src/components/pages/payment/payment/logic/common/extracDetails'
import { PaymentTypeArrayReservation } from 'src/components/pages/sales/reservation/logic/payment/logic/PaymentTypeArrayReservation'
import { getAirports } from '../../air/AirScaleRow'

const FormWrapperDialogPaymentItem = ({ children, onMethodsChange }) => {
  const methods = useForm({
    defaultValues: {},
  })

  useEffect(() => {
    if (onMethodsChange) {
      onMethodsChange(methods)
    }
  }, [])

  return <FormProvider {...methods}>{children}</FormProvider>
}

const FormWrapperPaymentArray = ({ selectedWorkUnit, selectedBusinessUnit, currency, item }) => {
  const [rowStyle, setRowStyle] = useState({})
  const { setCurrency } = usePaymentContext()
  const { setValue } = useFormContext()

  useEffect(() => {
    setCurrency(currency)
  }, [currency])

  useEffect(() => {
    setValue('paymentType', [
      {
        payment_type: null,
        id_payment: null,
        currency: currency?.hash_currency,
        apply_extra_cost: true,
        date: new Date(),
        amount_to_pay: null,
      },
    ])
  }, [])

  const [initialValues, setInitialValues] = useState([
    {
      payment_type: null,
      amount_to_pay: null,
      apply_extra_cost: null,
      card: null, // card props
      financial_entity: null, // card props
      quota: null,
    },
  ])
  const [extraCostSuccessStates, setExtraCostSuccessStates] = useState({})
  return (
    <PaymentTypeArrayReservation
      selectedWorkUnit={selectedWorkUnit}
      selectedBusinessUnit={selectedBusinessUnit}
      rowStyle={rowStyle}
      setRowStyle={setRowStyle}
      initialValues={initialValues}
      setInitialValues={setInitialValues}
      extraCostSuccessStates={extraCostSuccessStates}
      setExtraCostSuccessStates={setExtraCostSuccessStates}
      item={item}
    />
  )
}

const getTotal = (paymentType) => {
  try {
    return paymentType
      .map((payment) => payment?.extra_cost)
      .flat()
      .filter((cost) => cost?.name === 'net_amount')
      .reduce((sum, current) => sum + current?.value, 0)
    // Filtrar solo los objetos con name 'net_amount'
    // Sumar los valores de esos objetos
  } catch (error) {
    return 0
  }
}

const ConfirmButtonToPay = ({
  selectedItems,
  paymentCurrecies,
  hash_business_unit,
  currency,
  onClosePaymentItemModal,
  setDisableCurrency
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const paymentType = useWatch({ name: 'paymentType' })
  const { reset } = useFormContext()
  const [hasAmount, setHasAmount] = useState(false)
  const [loadingGeneratePayment, setLoadingGeneratePayment] = useState(false)
  // const [loadingGeneratePaymentWithReceipt, setLoadingGeneratePaymentWithReceipt] = useState(false) comentado ya que por ahora este tipo de pago no necesita esta logica

  const areAllExtraCostsSuccess = (payments) => {
    return (
      payments &&
      Array.isArray(payments) &&
      payments?.every(
        (payment) =>
          payment?.extra_cost &&
          Array.isArray(payment?.extra_cost) &&
          payment.extra_cost?.every((cost) => cost?.success === true)
      )
    )
  }

  useEffect(() => {
    if (paymentType) {
      if(paymentType.some((item)=>item.payment_type)){
        setDisableCurrency(true)
      } else {
        setDisableCurrency(false)
      }
      setHasAmount(paymentType.some((item) => item.amount_to_pay > 0) && areAllExtraCostsSuccess(paymentType))
      return
    }
    setHasAmount(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType])

  const onErrorGeneratePayment = () => {
    notify('ra.message.item_payment_generate_error', {
      type: 'error',
    })
    onClosePaymentItemModal()
    reset()
  }

  const onSuccessGeneratePayment = () => {
    notify('ra.message.item_payment_generate_success', {
      type: 'success',
    })
    onClosePaymentItemModal()
    reset()
  }

  const generatePayment = async () => {
    try {
      const addAmountToPayInItem = (selectedItems) => {
        if (Array.isArray(selectedItems) && selectedItems) {
          const item = selectedItems[0]
          return [
            {
              ...item,
              amount_to_pay: getTotal(paymentType),
            },
          ]
        }
      }
      const body = {
        paymentType: extractDetails(paymentType),
        credit: [],
        currencies_exchange: reverseExchangeRate(paymentCurrecies),
        hash_business_unit: hash_business_unit,
        amount: getTotal(paymentType),
        generate_receipt: true,
        item: addAmountToPayInItem(selectedItems) || [],
        currency: currency,
      }
      setLoadingGeneratePayment(true)
      const generatePayment = await paymentServices.liquidateAirExpenses(body)
      if (generatePayment) {
        onSuccessGeneratePayment()
      }
    } catch (error) {
      onErrorGeneratePayment()
    } finally {
      setLoadingGeneratePayment(false)
      customAwait([refresh])
    }
  }

  // const generatePaymentWithReceipt = async () => { comentado ya que por ahora este tipo de pago no necesita esta logica
  //   try {
  //     const body = {
  //       paymentType: extractDetails(paymentType),
  //       credit: [],
  //       currencies_exchange: reverseExchangeRate(paymentCurrecies),
  //       hash_business_unit: hash_business_unit,
  //       amount: getTotal(paymentType),
  //       generate_receipt: true,
  //       item: selectedItems || [],
  //       currency: currency,
  //     }
  //     setLoadingGeneratePaymentWithReceipt(true)
  //     const generatePayment = await paymentServices.liquidateAirExpenses(body)
  //     if (generatePayment) {
  //       onSuccessGeneratePayment()
  //     }
  //   } catch (error) {
  //     onErrorGeneratePayment()
  //   } finally {
  //     setLoadingGeneratePaymentWithReceipt(false)
  //     customAwait([refresh])
  //   }
  // }

  const total_amount_to_pay_item = (selectedItems || []).reduce(
    (total, record) => total + Number(record?.amount_pending_converted || 0),
    0
  )

  const amountToPayGreaterThanAmountItem = (total_amount_to_pay_payment_type, total_amount_to_pay_item) => {
    try {
      if (total_amount_to_pay_payment_type > total_amount_to_pay_item) {
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  const isValidAmountTotalPasseger = (payments) => {
    if (!Array.isArray(payments)) return false // Initial validation to ensure payments is an array.

    return payments.every((payment) => {
      if (!payment?.passenger || !Array.isArray(payment.passenger)) {
        // If there are no passengers or it's not an array, no issue, proceed to the next payment.
        return true
      }

      // Calculate the total sum of passenger amounts.
      const totalPassengerAmount = payment.passenger.reduce(
        (sum, passenger) => sum + (passenger?.amount || 0),
        0
      )

      // Validate that the total sum of passenger amounts does not exceed the payment amount.
      return totalPassengerAmount <= (payment.amount_to_pay || 0)
    })
  }

  return (
    <Box flex={1} display={'flex'} justifyContent={'flex-end'}>
      {/* comentado ya que por ahora este tipo de pago no necesita esta logica
        <HasPermission permission={PERMISSION_RECEIPT} resource={'voucher'}>
          <LoadingButton
            className='button'
            variant='contained'
            disabled={
              !hasAmount ||
              amountToPayGreaterThanAmountItem(getTotal(paymentType), total_amount_to_pay_item) ||
              !isValidAmountTotalPasseger(paymentType)
            }
            onClick={generatePaymentWithReceipt}
            loading={loadingGeneratePaymentWithReceipt}
            style={{ margin: '10px' }}
          >
            {translate('ra.action.finish_and_generate_receipt')}
          </LoadingButton> 
        </HasPermission>
      */}
      <LoadingButton
        className='button'
        variant='contained'
        disabled={
          !hasAmount ||
          amountToPayGreaterThanAmountItem(getTotal(paymentType), total_amount_to_pay_item) ||
          !isValidAmountTotalPasseger(paymentType)
        }
        onClick={generatePayment}
        loading={loadingGeneratePayment}
        style={{ margin: '10px' }}
      >
        {translate('ra.action.finish')}
      </LoadingButton>
    </Box>
  )
}

export const ModalPayAirItem = ({
  openPaymentAirItemModal,
  setOpenPaymentAirItemModal,
  reservation,
  selectedItems,
  setSelectedItems,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [selectedCreditItemsToCreditData, setSelectedItemsToCreditData] = useState([])
  const [paymentCurrecies, setPaymentCurrecies] = useState([])
  const [curreciesByBusinessUnit, setCurreciesByBusinessUnit] = useState([])
  const [currencySelected, setCurrencySelected] = useState({})
  const [loadingBusinessUnitToCurrency, setLoadingBusinessUnitToCurrency] = useState(false)
  const [loadingPaymentCurrencies, setLoadingPaymentCurrencies] = useState(false)
  const [openExchangeModal, setOpenExchangeModal] = useState(false)
  const [loadingItemRecalculate, setLoadingItemRecalculate] = useState(false)
  const [isChangeExchange, setIsChangeExchange] = useState(false)
  const [methods, setMethods] = useState(null)
  const [disableCurrency, setDisableCurrency] = useState(false)

  useEffect(() => {
    getPaymentCurrencies()
  }, [])

  useEffect(() => {
    if (reservation?.business_unit_relation?.id) {
      getCurrenciesByBusinessUnitById(reservation?.business_unit_relation?.id)
    }
  }, [reservation?.business_unit_relation?.id])

  const fIlterCurrencyForUATP = (currencies) => {
    try {
      const short_name_EUR = 'EUR'
      const short_name_BRL = 'BRL'
      const currencies_filter = currencies.filter(
        (currencies) => currencies?.short_name != short_name_EUR && currencies?.short_name != short_name_BRL
      )
      return currencies_filter
    } catch (error) {
      return currencies
    }
  }

  const getCurrenciesByBusinessUnitById = async (selectedBusinessUnitId) => {
    try {
      if (!selectedBusinessUnitId) {
        notify('ra.message.has_not_business_unit_to_get_currencies', {
          type: 'error',
        })
        setCurreciesByBusinessUnit([])
        return
      }
      setLoadingBusinessUnitToCurrency(true)
      const currencies = await businessUnitServices.currencies_by_business_unit(selectedBusinessUnitId)
      if (currencies) {
        const filteredCurrencies = fIlterCurrencyForUATP(currencies)
        setCurreciesByBusinessUnit(filteredCurrencies)
      } else {
        notify('ra.message.has_not_currencies_by_business_unit', {
          type: 'error',
        })
        setCurreciesByBusinessUnit([])
      }
    } catch (error) {
      notify('ra.message.has_not_currencies_by_business_unit', {
        type: 'error',
      })
      setCurreciesByBusinessUnit([])
    } finally {
      setLoadingBusinessUnitToCurrency(false)
    }
  }

  const getPaymentCurrencies = async () => {
    try {
      const exchange = await getCurrencyByUser()
      setPaymentCurrecies(exchange)
    } catch (error) {
      setPaymentCurrecies([])
    }
  }

  const getCurrencyByUser = async () => {
    try {
      setLoadingPaymentCurrencies(true)
      const currencies = await userServices.getCurrencyByUser()
      if (currencies) {
        const format = currencies.filter((currency) => !currency?.is_reverse)
        return format
      } else {
        return []
      }
    } catch (error) {
      return []
    } finally {
      setLoadingPaymentCurrencies(false)
    }
  }

  const onClosePaymentItemModal = () => {
    setCurrencySelected({})
    setOpenPaymentAirItemModal(false)
    setSelectedItems(null)
    if (methods) {
      methods.reset()
      setMethods(null)
    }
  }

  const onChangeCurrency = (e) => {
    let currency = e.target.value
    if (currency) {
      delete currency.business_unit_relation
      delete currency.business_unit_to_relation
      delete currency.work_unit_relation
      delete currency.work_unit_to_relation
      setCurrencySelected(currency)
      calculateAmountOfItems(
        reservation,
        selectedItems,
        paymentCurrecies,
        currency?.hash_currency,
        currency?.hash_currency != undefined
      )
    } else {
      setCurrencySelected({})
    }
  }

  const calculateAmountOfItems = async (
    reservation,
    selectedCreditItemsToCreditData,
    paymentCurrecies,
    currency_hash,
    isPaymentItem = false
  ) => {
    try {
      setLoadingItemRecalculate(true)
      const currencies_with_reverse = reverseExchangeRate(paymentCurrecies)
      let items = await itemServices.item_to_pay([reservation?.id], currency_hash, currencies_with_reverse)
      if (items) {
        const item_with_amount_pending_converted = formatData(items)
        const mergedList = (isPaymentItem ? selectedItems || [] : selectedCreditItemsToCreditData).map(
          (item1) => {
            const match = item_with_amount_pending_converted.find((item2) => item2.item_id === item1.item_id)
            if (match) {
              return {
                ...item1,
                amount_total_converted: match.amount_total_converted,
                amount_pending_converted: match.amount_pending_converted,
              }
            }
            return item1
          }
        )
        if (isPaymentItem) {
          // determinar si es el flujo para pagar un item o pagar con credito
          setSelectedItems(mergedList)
        } else {
          setSelectedItemsToCreditData(mergedList)
        }
      }
    } catch (error) {
      console.log('error', error)
      notify('ra.message.reservation_item_not_found', {
        type: 'error',
      })
    } finally {
      setLoadingItemRecalculate(false)
    }
  }

  const formatData = (data) => {
    let auxData = [...data]
    return auxData
      .filter((item) => item?.amount_pending)
      .map((item) => {
        return {
          amount_to_pay: 0,
          item_id: item.item_id,
          id: item.item_id,
          amount_total_converted: item.amount_total_converted,
          amount_pending_converted: item.amount_pending_converted,
          hash_item: item?.hash_item,
          currency_converted: item.currency_converted,
        }
      })
  }

  const getProductName = (item) => {
    if (item?.product?.ref_table_detail === 'hotel_detail') {
      return item?.name
    } else if (item?.product?.ref_table_detail === 'car_detail') {
      return item?.provider
    } else if (
      item?.product?.ref_table_detail === 'assistance_detail' ||
      item?.product?.ref_table_detail === 'other_detail'
    ) {
      return `${item.destination} / ${item.name}`
    } else if (item?.product?.ref_table_detail === 'transfer_detail') {
      return item?.pickup_origin
    } else if (item?.product?.ref_table_detail === 'air_detail') {
      return getAirports(item?.segments?.[0]?.itineraryGroup)
    } else if (item?.product?.ref_table_detail === 'train_detail_segment') {
      return `${item?.segments?.[0]?.origin_name}/${item?.segments?.[0]?.arrival_name}`
    } else if (item?.product?.ref_table_detail === 'train_detail_passes') {
      return item?.passes?.[0]?.company_name
    }
  }

  const amountToPayGreaterThanAmountItem = (total_amount_to_pay_payment_type, total_amount_to_pay_item) => {
    try {
      if (total_amount_to_pay_payment_type > total_amount_to_pay_item) {
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  const invalidAmountErrorColor = (total_amount_to_pay_payment_type, total_amount_to_pay_item) => {
    const RED = 'red'
    const INHERIT = 'inherit'
    if (amountToPayGreaterThanAmountItem(total_amount_to_pay_payment_type, total_amount_to_pay_item)) {
      return RED
    }
    return INHERIT
  }

  const invalidAmountErrorMessage = (total_amount_to_pay_payment_type, total_amount_to_pay_item) => {
    if (amountToPayGreaterThanAmountItem(total_amount_to_pay_payment_type, total_amount_to_pay_item)) {
      return translate('ra.message.validate_amount_to_use')
    }
  }

  const handleCurrencyChange = (index, field, value) => {
    setIsChangeExchange(true)
    const updatedCurrencies = [...paymentCurrecies]
    updatedCurrencies[index] = { ...updatedCurrencies[index], [field]: parseFloat(value) }
    setPaymentCurrecies(updatedCurrencies)
  }

   const onCloseExhangeModal = () => {
     setOpenExchangeModal(false)
     isChangeExchange &&
       calculateAmountOfItems(
         reservation,
         selectedCreditItemsToCreditData || selectedItems,
         paymentCurrecies,
         currencySelected?.hash_currency,
         currencySelected?.hash_currency != undefined
       )
     setIsChangeExchange(false)
   }

  return (
    <>
      {openPaymentAirItemModal && (
        <FormWrapperDialogPaymentItem onMethodsChange={setMethods}>
          <Dialog fullWidth maxWidth='lg' open={openPaymentAirItemModal} onClose={onClosePaymentItemModal}>
            <DialogTitle>{translate('ra.title.payment_register')}</DialogTitle>

            <DialogContent>
              <Paper sx={{ borderRadius: '10px' }}>
                <TableContainer sx={{ marginY: '30px' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{translate('ra.field.item_descripcion')}</TableCell>
                        <TableCell>{translate('ra.field.locator')}</TableCell>
                        <TableCell>{translate('ra.field.currency')}</TableCell>
                        <TableCell>{translate('ra.field.amount_pending')}</TableCell>
                        {currencySelected?.hash_currency && (
                          <>
                            <TableCell>
                              {translate('ra.field.amount_pending_in', {
                                currency: currencySelected.short_name,
                              })}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {false ? (
                        <TableRow>
                          {[...Array(5)].map((_, i) => (
                            <TableCell key={i}>
                              <Skeleton variant='rectangular' width={'100%'} height={20} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ) : (
                        <>
                          {(selectedItems || []).map((row, i) => (
                            <TableRow key={i}>
                              <TableCell>{getProductName(row)}</TableCell>
                              <TableCell>{row?.code}</TableCell>
                              <TableCell>{row?.currency}</TableCell>
                              <TableCell>
                                {numberFormatterWithDecimalAndThousand(row?.amount_pending)}
                              </TableCell>
                              {currencySelected?.hash_currency && (
                                <>
                                  <TableCell>
                                    {loadingItemRecalculate ? (
                                      <>
                                        <Skeleton variant='rectangular' width={60} height={20} />
                                      </>
                                    ) : (
                                      numberFormatterWithDecimalAndThousand(row?.amount_pending_converted)
                                    )}
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Grid container item>
                <Grid item md={8} lg={8}>
                  <Box style={{ margin: '20px' }}>
                    <EditInputsWrap label={translate(`ra.field.currency`)} md={8} lg={8} required>
                      {loadingBusinessUnitToCurrency ? (
                        <Skeleton variant='rectangular' width={300} height={40} />
                      ) : (
                        <FormControl fullWidth>
                          <Select
                            labelId='currencies'
                            id='currencies'
                            disabled = {disableCurrency}
                            value={currencySelected}
                            onChange={onChangeCurrency}
                            size='small'
                          >
                            {curreciesByBusinessUnit.map((currency, i) => (
                              <MenuItem key={i} value={currency}>
                                {currency?.symbol} - {currency?.long_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </EditInputsWrap>
                  </Box>
                </Grid>
                <Grid item md={4} lg={4}>
                  {paymentCurrecies.length > 0 && (
                    <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                      <Box className='currency_resume'>
                        {loadingPaymentCurrencies
                          ? Array.from(new Array(3)).map((_, i) => (
                              <Box key={i} mb={2} display={'flex'}>
                                <Skeleton variant='text' width={30} height={10} /> -
                                <Skeleton variant='text' width={30} height={10} />:
                                <Skeleton variant='text' width={30} height={10} />
                              </Box>
                            ))
                          : paymentCurrecies.map((currency, i) => (
                              <Box key={i}>
                                <Typography component='span' fontWeight='bold'>
                                  {currency?.currency_relation?.short_name}-
                                  {currency?.currency_to_relation?.short_name} : {currency.value}
                                </Typography>
                              </Box>
                            ))}
                      </Box>
                      {hasPermissionFunction('currency-by-work-unit', PERMISSION_EDIT) && (
                        <Box display={'flex'}>
                          <Button onClick={() => setOpenExchangeModal(true)}>
                            <EditIcon />
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Divider variant='fullWidht' />
              <Grid container item>
                <Grid item md={12} lg={12} sx={{ marginTop: '20px' }}>
                  <FormDataConsumer>
                    {({ formData }) => {
                      if (formData?.paymentType) {
                        const total_amount_to_pay_payment_type = getTotal(formData?.paymentType)

                        const total_amount_to_pay_item = (selectedItems || []).reduce(
                          (total, record) => total + Number(record?.amount_pending_converted || 0),
                          0
                        )

                        return (
                          <Box display={'flex'} flexDirection={'column'}>
                            <Typography variant='h6' component='h6'>
                              {translate('ra.field.amount_pending_to_pay_in', {
                                currency: currencySelected?.short_name,
                              })}
                              :{' '}
                              {numberFormatterWithDecimalAndThousand(
                                total_amount_to_pay_item - total_amount_to_pay_payment_type
                              )}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                variant='h6'
                                component='h6'
                                style={{
                                  color: invalidAmountErrorColor(
                                    total_amount_to_pay_payment_type,
                                    total_amount_to_pay_item
                                  ),
                                }}
                              >
                                {translate('ra.field.amount_to_apply')} {currencySelected?.short_name} :{' '}
                                {numberFormatterWithDecimalAndThousand(total_amount_to_pay_payment_type)}
                              </Typography>
                              {invalidAmountErrorMessage(
                                total_amount_to_pay_payment_type,
                                total_amount_to_pay_item
                              ) && (
                                <Typography
                                  variant='h7'
                                  component='h7'
                                  style={{
                                    marginLeft: '20px',
                                    color: invalidAmountErrorColor(
                                      total_amount_to_pay_payment_type,
                                      total_amount_to_pay_item
                                    ),
                                  }}
                                >
                                  {invalidAmountErrorMessage(
                                    total_amount_to_pay_payment_type,
                                    total_amount_to_pay_item
                                  )}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )
                      }
                    }}
                  </FormDataConsumer>
                </Grid>
              </Grid>
              {currencySelected?.hash_currency && (
                <>
                  <Grid container item>
                    <Grid item style={{ margin: '30px 0 30px 0' }}>
                      <PaymentProviderContext>
                        <FormWrapperPaymentArray
                          key={currencySelected?.hash_currency}
                          selectedBusinessUnit={reservation?.business_unit_relation}
                          selectedWorkUnit={reservation?.work_unit_relation}
                          currency={currencySelected}
                          item={selectedItems?.[0]}
                        />
                      </PaymentProviderContext>
                    </Grid>
                  </Grid>

                  <Divider variant='fullWidht' />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={onClosePaymentItemModal}>
                {translate('ra.action.cancel')}
              </Button>
              <ConfirmButtonToPay
                selectedItems={selectedItems}
                paymentCurrecies={paymentCurrecies}
                hash_business_unit={reservation?.business_unit_relation?.hash_business_unit}
                currency={currencySelected}
                onClosePaymentItemModal={onClosePaymentItemModal}
                setDisableCurrency={setDisableCurrency}
              />
            </DialogActions>
          </Dialog>
        </FormWrapperDialogPaymentItem>
      )}
      {openExchangeModal && (
        <Dialog fullWidth open={openExchangeModal} onClose={onCloseExhangeModal}>
          <DialogTitle>{translate('ra.entity.payment-entity')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              {paymentCurrecies.map((currency, i) => (
                <Grid item xs={12} key={i}>
                  <Box display='flex' alignItems='center'>
                    <Typography component='span' fontWeight='bold'>
                      {currency?.currency_relation?.short_name}-{currency?.currency_to_relation?.short_name} :
                    </Typography>
                    <TextField
                      defaultValue={currency.value}
                      onChange={(e) => handleCurrencyChange(i, 'value', e.target.value)}
                      size='small'
                      type='number'
                      sx={{ ml: 2, width: '140px' }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={onCloseExhangeModal}>
              {translate('ra.action.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
